<template>
    <v-dialog
        :model-value="modelValue"
        max-width="900"
        width="92vw"
        persistent
        @update:model-value="$emit('update:modelValue', $event)">
        <v-card class="transport-formula-card">
            <v-toolbar density="comfortable" color="surface">
                <v-toolbar-title class="d-flex align-center">
                    Calculator cost transport + taxe
                    <span
                        v-if="!readOnly"
                        class="transport-collapse__info ml-2"
                        @click.stop.prevent>
                        <v-icon
                            size="small"
                            color="medium-emphasis"
                            icon="mdi-information-outline" />
                        <v-tooltip
                            activator="parent"
                            location="bottom"
                            max-width="360">
                            {{ calculatorOverviewInfo }}
                        </v-tooltip>
                    </span>
                </v-toolbar-title>
                <v-spacer />
                <v-btn
                    v-if="!readOnly"
                    variant="text"
                    prepend-icon="mdi-restore"
                    class="me-1"
                    title="Golește calculatorul. Apoi Aplică pentru a ieși din modul formulă (păstrează % din builder)."
                    @click="resetAllToFresh">
                    Resetează
                </v-btn>
                <v-btn icon variant="text" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-card-text class="pt-4">
                <v-row dense class="mb-3">
                    <v-col cols="12" md="3">
                        <v-text-field
                            :model-value="supplierListPriceDisplay"
                            label="Preț listă furnizor"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            readonly
                            hint="Din produs — baza calculului"
                            persistent-hint />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field
                            :model-value="workingCurrencyLabel"
                            label="Monedă costuri"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            readonly
                            hint="Din detalii furnizor"
                            persistent-hint />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field
                            :model-value="draft.goodsQuantity"
                            label="Cantitate"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            inputmode="decimal"
                            autocomplete="off"
                            :readonly="readOnly"
                            :hint="goodsQuantityHint"
                            persistent-hint
                            :error-messages="goodsFieldErrors.goodsQuantity || []"
                            @update:model-value="onGoodsQuantityChanged" />
                    </v-col>
                    <v-col cols="12" md="2">
                        <v-text-field
                            :model-value="draft.goodsDiscount"
                            label="Discount (%)"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            inputmode="decimal"
                            autocomplete="off"
                            :readonly="readOnly"
                            :hint="goodsDiscountHint"
                            persistent-hint
                            :error-messages="goodsFieldErrors.goodsDiscount || []"
                            @update:model-value="onGoodsDiscountChanged" />
                    </v-col>
                    <v-col cols="12" md="3">
                        <v-text-field
                            :model-value="draft.goodsValue"
                            :label="`Valoare marfă (${workingCurrencyLabel})`"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                            inputmode="decimal"
                            autocomplete="off"
                            :readonly="readOnly"
                            :hint="goodsValueHint"
                            persistent-hint
                            :error-messages="goodsFieldErrors.goodsValue || []"
                            @update:model-value="onGoodsValueManualEdit">
                            <template v-if="!readOnly && draft.goodsValueIsManual" #append-inner>
                                <v-btn
                                    icon="mdi-refresh"
                                    size="x-small"
                                    variant="text"
                                    title="Recalculează din preț × cantitate (− discount)"
                                    @click.stop="resetGoodsValueToCalculated" />
                            </template>
                        </v-text-field>
                    </v-col>
                </v-row>
                <v-divider />
                <v-row dense class="mt-3 mb-1">
                    <v-col cols="12" md="3">
                        <div
                            class="transport-info-block pa-3 rounded border"
                            :class="{ 'transport-info-block--error': totalCostsErrorMessages.length }">
                            <div class="text-caption text-medium-emphasis">
                                Total costuri ({{ workingCurrencyLabel }})
                            </div>
                            <div class="text-body-1 font-weight-medium mt-1">
                                {{ summary.totalDisplay }}
                            </div>
                            <div
                                v-if="totalCostsErrorMessages.length"
                                class="text-caption text-error mt-1">
                                {{ totalCostsErrorMessages[0] }}
                            </div>
                            <div
                                v-else
                                class="text-caption text-medium-emphasis mt-1">
                                {{ totalCostsHint }}
                            </div>
                        </div>
                    </v-col>
                    <v-col cols="12" md="3">
                        <div
                            class="transport-info-block pa-3 rounded border"
                            :class="{ 'transport-info-block--error': transportRateErrorMessages.length }">
                            <div class="text-caption text-medium-emphasis">
                                % transport
                            </div>
                            <div class="text-body-1 font-weight-medium mt-1">
                                {{ summary.rateDisplay }}
                            </div>
                            <div
                                v-if="transportRateErrorMessages.length"
                                class="text-caption text-error mt-1">
                                {{ transportRateErrorMessages[0] }}
                            </div>
                            <div
                                v-else
                                class="text-caption text-medium-emphasis mt-1">
                                {{ transportRateHint }}
                            </div>
                        </div>
                    </v-col>
                </v-row>
                <v-alert
                    v-if="applyBlockedMessage"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mt-2 mb-1 text-body-2">
                    {{ applyBlockedMessage }}
                </v-alert>

                <div class="transport-collapse mb-3 mt-3">
                    <button
                        type="button"
                        class="transport-collapse__header"
                        @click="costsSectionOpen = !costsSectionOpen">
                        <v-icon size="small" class="mr-1">
                            {{ costsSectionOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                        </v-icon>
                        <span class="text-subtitle-2 font-weight-medium">
                            Costuri transport
                        </span>
                        <span
                            class="transport-collapse__info ml-1"
                            @click.stop.prevent>
                            <v-icon
                                size="small"
                                color="medium-emphasis"
                                icon="mdi-information-outline" />
                            <v-tooltip
                                activator="parent"
                                location="top"
                                max-width="340">
                                {{ costsSectionInfo }}
                            </v-tooltip>
                        </span>
                        <span class="transport-collapse__hint text-caption text-medium-emphasis ml-2">
                            {{ workingCurrencyLabel }}
                            <template v-if="!costsSectionOpen">
                                · {{ costsCollapsedHint }}
                            </template>
                        </span>
                        <v-spacer />
                        <span class="text-caption text-medium-emphasis">
                            {{ costsSectionOpen ? 'Ascunde' : 'Arată' }}
                        </span>
                    </button>
                    <div v-show="costsSectionOpen" class="transport-collapse__body pt-2">
                        <div
                            v-for="(line, index) in regularCostLines"
                            :key="line.key"
                            class="mb-1">
                            <div class="pa-2 rounded border">
                                <v-row dense align="center">
                                    <v-col cols="12" md="5">
                                        <v-text-field
                                            v-model="line.label"
                                            label="Denumire"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                            :readonly="readOnly" />
                                    </v-col>
                                    <v-col cols="12" md="4">
                                        <v-text-field
                                            v-model="line.value"
                                            :label="`Sumă (${workingCurrencyLabel})`"
                                            variant="outlined"
                                            density="compact"
                                            hide-details
                                            inputmode="decimal"
                                            autocomplete="off"
                                            :readonly="readOnly">
                                            <template #append-inner>
                                                <span class="text-caption text-medium-emphasis">
                                                    {{ workingCurrencyLabel }}
                                                </span>
                                            </template>
                                        </v-text-field>
                                    </v-col>
                                    <v-col cols="10" md="2">
                                        <div class="text-body-2 text-medium-emphasis">
                                            = {{ formatLineAmount(line.key) }}
                                        </div>
                                        <div
                                            v-if="lineSourceCaption(line)"
                                            class="text-caption text-medium-emphasis">
                                            {{ lineSourceCaption(line) }}
                                        </div>
                                    </v-col>
                                    <v-col cols="2" md="1" class="d-flex justify-end">
                                        <v-btn
                                            icon="mdi-close"
                                            size="small"
                                            variant="text"
                                            :disabled="readOnly"
                                            @click="removeLine(lineIndexInDraft(line.key))" />
                                    </v-col>
                                </v-row>
                            </div>

                            <div
                                v-if="!readOnly && index === regularCostLines.length - 1"
                                class="d-flex justify-center py-1">
                                <v-btn
                                    icon
                                    size="x-small"
                                    variant="tonal"
                                    color="primary"
                                    title="Adaugă cost"
                                    @click="addLineAfter(lineIndexInDraft(line.key))">
                                    <v-icon size="small">mdi-plus</v-icon>
                                </v-btn>
                            </div>
                        </div>

                        <div
                            v-if="!readOnly && !regularCostLines.length"
                            class="d-flex justify-center py-2">
                            <v-btn
                                variant="tonal"
                                color="primary"
                                prepend-icon="mdi-plus"
                                @click="addLineAfter(-1)">
                                Adaugă cost
                            </v-btn>
                        </div>
                    </div>
                </div>

                <div class="transport-collapse mt-3 mb-3">
                    <button
                        type="button"
                        class="transport-collapse__header"
                        @click="converterSectionOpen = !converterSectionOpen">
                        <v-icon size="small" class="mr-1">
                            {{ converterSectionOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                        </v-icon>
                        <span class="text-subtitle-2 font-weight-medium">
                            Conversie valută
                        </span>
                        <span
                            class="transport-collapse__info ml-1"
                            @click.stop.prevent>
                            <v-icon
                                size="small"
                                color="medium-emphasis"
                                icon="mdi-information-outline" />
                            <v-tooltip
                                activator="parent"
                                location="top"
                                max-width="340">
                                {{ converterSectionInfo }}
                            </v-tooltip>
                        </span>
                        <span
                            v-if="!converterSectionOpen && converterCollapsedHint"
                            class="transport-collapse__hint text-caption text-medium-emphasis ml-2">
                            {{ converterCollapsedHint }}
                        </span>
                        <v-spacer />
                        <span class="text-caption text-medium-emphasis">
                            {{ converterSectionOpen ? 'Ascunde' : 'Arată' }}
                        </span>
                    </button>
                    <div v-show="converterSectionOpen" class="transport-collapse__body pt-2">
                        <v-row dense class="align-center">
                            <v-col cols="6" md="2">
                                <v-select
                                    v-model="draft.converterFromCurrency"
                                    :items="currencyOptions"
                                    item-title="title"
                                    item-value="value"
                                    label="Din"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    :readonly="readOnly" />
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-text-field
                                    :model-value="workingCurrencyLabel"
                                    label="În"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    readonly
                                    hint="Moneda din detalii furnizor"
                                    persistent-hint />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    :model-value="converterRateDisplay"
                                    :label="`Convertor ${fromCurrencyLabel} → ${toCurrencyLabel}`"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    readonly
                                    :loading="converterLoading" />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    v-model="sourceAmount"
                                    :label="`Sumă ${fromCurrencyLabel}`"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    inputmode="decimal"
                                    autocomplete="off"
                                    :readonly="readOnly" />
                            </v-col>
                            <v-col cols="12" md="3" class="d-flex">
                                <v-btn
                                    block
                                    variant="tonal"
                                    color="primary"
                                    :disabled="readOnly || converterLoading || !canConvertAmount"
                                    :loading="converterLoading"
                                    @click="convertAmount">
                                    Convertește în {{ toCurrencyLabel }}
                                </v-btn>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    :model-value="convertedAmountDisplay"
                                    :label="`Sumă ${workingCurrencyLabel}`"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    readonly>
                                    <template #append-inner>
                                        <span class="text-caption text-medium-emphasis">
                                            {{ workingCurrencyLabel }}
                                        </span>
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="8" class="d-flex align-center justify-end">
                                <v-btn
                                    variant="outlined"
                                    color="primary"
                                    :disabled="readOnly || !(convertedAmount > 0)"
                                    @click="addConvertedAsCost">
                                    Adaugă la costuri
                                </v-btn>
                            </v-col>
                            <v-col v-if="converterMeta" cols="12" class="pt-0">
                                <div class="text-caption text-medium-emphasis">
                                    {{ converterMeta }}
                                </div>
                            </v-col>
                            <v-col v-if="convertWarning" cols="12">
                                <div class="text-caption text-warning">{{ convertWarning }}</div>
                            </v-col>
                            <v-col v-if="convertError" cols="12">
                                <div class="text-caption text-error">{{ convertError }}</div>
                            </v-col>
                        </v-row>
                    </div>
                </div>
                <div
                    v-if="customsDutyLine"
                    class="mt-3 mb-3 transport-collapse customs-duty-block"
                    :class="{ 'customs-duty-block--disabled': !customsDutyLine.enabled }">
                    <div class="transport-collapse__header transport-collapse__header--customs">
                        <button
                            type="button"
                            class="transport-collapse__toggle"
                            @click="customsSectionOpen = !customsSectionOpen">
                            <v-icon size="small" class="mr-1">
                                {{ customsSectionOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                            </v-icon>
                            <span class="text-subtitle-2 font-weight-medium">
                                Taxe vamale
                            </span>
                            <span
                                class="transport-collapse__info ml-1"
                                @click.stop.prevent>
                                <v-icon
                                    size="small"
                                    color="medium-emphasis"
                                    icon="mdi-information-outline" />
                                <v-tooltip
                                    activator="parent"
                                    location="top"
                                    max-width="340">
                                    {{ customsSectionInfo }}
                                </v-tooltip>
                            </span>
                            <span
                                v-if="!customsSectionOpen"
                                class="transport-collapse__hint text-caption text-medium-emphasis ml-2">
                                {{ customsCollapsedHint }}
                            </span>
                        </button>
                        <v-switch
                            v-model="customsDutyLine.enabled"
                            label="Activ"
                            density="compact"
                            hide-details
                            color="primary"
                            class="ml-2 flex-grow-0"
                            :readonly="readOnly"
                            :disabled="readOnly"
                            @update:model-value="onCustomsEnabledChanged" />
                    </div>
                    <div v-show="customsSectionOpen" class="transport-collapse__body pt-2">
                        <v-row dense align="center">
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="customsDutyLine.label"
                                    label="Denumire"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    readonly />
                            </v-col>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="customsDutyLine.value"
                                    label="Procent (%)"
                                    variant="outlined"
                                    density="compact"
                                    hide-details
                                    inputmode="decimal"
                                    autocomplete="off"
                                    :readonly="readOnly || !customsDutyLine.enabled" />
                            </v-col>
                            <v-col cols="12" md="3">
                                <div class="text-body-2 text-medium-emphasis">
                                    = {{ formatLineAmount(customsDutyLine.key) }}
                                </div>
                            </v-col>
                        </v-row>
                        <div class="mt-2 px-1">
                            <div class="d-flex flex-wrap ga-2">
                                <v-checkbox
                                    v-model="customsDutyLine.baseGoodsValue"
                                    label="Valoare marfă"
                                    density="compact"
                                    hide-details
                                    :readonly="readOnly || !customsDutyLine.enabled"
                                    :disabled="readOnly || !customsDutyLine.enabled" />
                                <v-checkbox
                                    v-for="baseLine in amountBaseLines(customsDutyLine.key)"
                                    :key="`base-${customsDutyLine.key}-${baseLine.key}`"
                                    :model-value="
                                        (customsDutyLine.baseLineKeys || []).includes(
                                            baseLine.key,
                                        )
                                    "
                                    :label="baseLine.label || 'Cost'"
                                    density="compact"
                                    hide-details
                                    :readonly="readOnly || !customsDutyLine.enabled"
                                    :disabled="readOnly || !customsDutyLine.enabled"
                                    @update:model-value="
                                        toggleBaseLine(
                                            customsDutyLineIndex,
                                            baseLine.key,
                                            $event,
                                        )
                                    " />
                            </div>
                        </div>
                    </div>
                </div>

            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-spacer />
                <v-btn variant="text" @click="close">Anulează</v-btn>
                <v-btn
                    v-if="!readOnly"
                    color="primary"
                    variant="flat"
                    @click="apply">
                    Aplică
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import {
    applyAutoTransportGoodsValue,
    calculateTransportCostBreakdown,
    cloneTransportCostBreakdown,
    createDefaultTransportCostBreakdown,
    createTransportCostLine,
    ensureCustomsDutyLine,
    ensurePercentFeeBases,
    isCustomsDutyLine,
    normalizeCurrencyCode,
    normalizeTransportCostBreakdown,
    orderTransportCostLines,
    resolveEffectiveTransportGoodsValue,
    resolveTransportConverterRate,
    TRANSPORT_COST_MODE_FORMULA,
    TRANSPORT_COST_MODE_PERCENT,
    TRANSPORT_CURRENCY_OPTIONS,
    TRANSPORT_LINE_TYPE_FIXED,
    transportAmountBaseLines,
    validateTransportGoodsFields,
} from '@/utils/transportCostBreakdown';
import { formatRonPrice } from '@/utils/utils';
import {
    formatNumericInputValue,
    parseNumericInput,
    ratioToPercentageInput,
} from '@/utils/priceListProductCalculator';
import apiClient from '@/utils/apiClient';

function currencyLabel(code) {
    const normalized = String(code || '').trim().toLowerCase();
    if (normalized === 'euro' || normalized === 'eur') {
        return 'EUR';
    }
    if (normalized === 'usd') {
        return 'USD';
    }
    if (normalized === 'pln') {
        return 'PLN';
    }
    if (normalized === 'ron') {
        return 'RON';
    }
    return String(code || '').toUpperCase() || '—';
}

export default {
    name: 'TransportCostFormulaModal',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        breakdown: {
            type: Object,
            default: () => ({}),
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        supplierListPriceNew: {
            type: [String, Number],
            default: '',
        },
        supplierCurrency: {
            type: String,
            default: 'ron',
        },
        pricingSettings: {
            type: Object,
            default: () => ({}),
        },
    },
    emits: ['update:modelValue', 'apply'],
    data() {
        return {
            draft: cloneTransportCostBreakdown(),
            sourceAmount: '',
            convertedAmount: 0,
            converterLoading: false,
            convertError: '',
            convertWarning: '',
            converterMeta: '',
            currencyOptions: TRANSPORT_CURRENCY_OPTIONS,
            currencyWatchReady: false,
            /** Blocks treating programmatic goodsValue writes as manual edits. */
            syncingAutoGoodsValue: false,
            costsSectionOpen: true,
            converterSectionOpen: false,
            customsSectionOpen: false,
            /**
             * After Resetează, one Aplică may exit formula mode (empty calculator).
             * Any field edit clears this — empty form alone never enables Aplică.
             */
            pendingExitFormulaApply: false,
            /** Show red field errors only after the user clicks Aplică. */
            showApplyValidation: false,
        };
    },
    computed: {
        transportGoodsContext() {
            return {
                supplierListPriceNew: this.supplierListPriceNew,
            };
        },
        computedBreakdown() {
            return calculateTransportCostBreakdown(
                this.draft,
                this.transportGoodsContext,
            );
        },
        supplierListPriceDisplay() {
            const price = parseNumericInput(this.supplierListPriceNew);
            if (!(price > 0)) {
                return '—';
            }
            return formatRonPrice(price, 4, this.supplierCurrency || 'ron');
        },
        goodsQuantityHint() {
            return 'Obligatoriu · cantitate > 0';
        },
        goodsDiscountHint() {
            return 'Opțional';
        },
        goodsValueHint() {
            if (this.draft.goodsValueIsManual) {
                return 'Valoare introdusă manual · trebuie > 0';
            }
            const calculated = resolveEffectiveTransportGoodsValue(
                this.draft,
                this.transportGoodsContext,
            );
            const discount = parseNumericInput(this.draft.goodsDiscount);
            if (discount >= 100) {
                return 'Discount 100% → valoare 0 (nu se poate calcula % transport)';
            }
            if (calculated > 0) {
                if (discount > 0) {
                    return 'Calculat: preț listă × cantitate × (1 − discount%)';
                }
                return 'Calculat: preț listă × cantitate (poți edita manual)';
            }
            return 'Introdu cantitatea (> 0) pentru calcul automat';
        },
        goodsValidation() {
            if (this.isExitFormulaApply) {
                return {
                    goodsQuantity: null,
                    goodsDiscount: null,
                    goodsValue: null,
                    valid: true,
                };
            }
            return validateTransportGoodsFields(
                this.draft,
                this.transportGoodsContext,
            );
        },
        goodsFieldErrors() {
            if (this.readOnly || !this.showApplyValidation || this.isExitFormulaApply) {
                return {
                    goodsQuantity: null,
                    goodsDiscount: null,
                    goodsValue: null,
                };
            }
            return this.goodsValidation;
        },
        goodsFieldsValid() {
            return this.goodsValidation.valid;
        },
        /** User entered something in goods fields (not a blank reset). */
        hasGoodsFieldInput() {
            return Boolean(
                String(this.draft?.goodsQuantity ?? '').trim()
                || String(this.draft?.goodsDiscount ?? '').trim()
                || String(this.draft?.goodsValue ?? '').trim(),
            );
        },
        /**
         * Only after Resetează: empty calculator may Aplică to exit formula mode.
         */
        isExitFormulaApply() {
            return (
                this.pendingExitFormulaApply
                && this.isCalculatorCleared
                && !this.hasGoodsFieldInput
            );
        },
        hasPositiveGoodsValue() {
            return this.computedBreakdown.goodsValue > 0;
        },
        hasPositiveTransportRate() {
            return this.computedBreakdown.rate > 0;
        },
        hasPositiveCostTotal() {
            return this.computedBreakdown.total > 0;
        },
        formulaApplyValid() {
            if (this.isExitFormulaApply) {
                return true;
            }
            return (
                this.hasPositiveGoodsValue
                && this.hasPositiveTransportRate
                && this.hasPositiveCostTotal
                && this.goodsFieldsValid
            );
        },
        totalCostsHint() {
            return 'Suma liniilor de cost (trebuie > 0)';
        },
        transportRateHint() {
            return 'Total costuri ÷ valoare marfă (trebuie > 0)';
        },
        costsSectionInfo() {
            return (
                'Adăugați liniile de cost în moneda furnizorului (ex.: transport, fee-uri). '
                + 'Sumele se adună în Total costuri. '
                + '% transport = Total costuri ÷ Valoare marfă.'
            );
        },
        converterSectionInfo() {
            return (
                'Opțional. Convertiți o sumă din altă valută în moneda costurilor, '
                + 'apoi apăsați „Adaugă la costuri”. '
                + 'Cursul se încarcă automat (BNR/ECB) sau din setările listei.'
            );
        },
        customsSectionInfo() {
            return (
                'Opțional. Activați, setați procentul, apoi bifați baza de calcul '
                + '(Valoare marfă și/sau linii de cost). '
                + 'Rezultatul = bază × procent și se adaugă la Total costuri.'
            );
        },
        calculatorOverviewInfo() {
            return (
                'Completați cantitatea > 0 și cel puțin un cost. Discountul este opțional. '
                + 'Valoare marfă = preț listă × cantitate × (1 − discount%) — trebuie > 0 pentru % transport.'
            );
        },
        totalCostsErrorMessages() {
            if (this.readOnly || !this.showApplyValidation || this.isExitFormulaApply) {
                return [];
            }
            if (!this.hasPositiveCostTotal) {
                return [
                    'Lipsește totalul costurilor. Adăugați cel puțin o linie de cost cu sumă > 0 (sau activați taxele vamale cu bază validă).',
                ];
            }
            return [];
        },
        transportRateErrorMessages() {
            if (this.readOnly || !this.showApplyValidation || this.isExitFormulaApply) {
                return [];
            }
            if (!this.hasPositiveGoodsValue) {
                return [
                    '% transport = total costuri ÷ valoare marfă. Completați o valoare marfă > 0 (cantitate și/sau preț listă).',
                ];
            }
            if (!this.hasPositiveCostTotal) {
                return [
                    '% transport nu se poate calcula fără costuri. Adăugați cel puțin un cost > 0.',
                ];
            }
            if (!this.hasPositiveTransportRate) {
                return [
                    '% transport rezultat este 0. Verificați că atât valoarea mărfii, cât și totalul costurilor sunt > 0.',
                ];
            }
            return [];
        },
        applyBlockedMessage() {
            if (this.readOnly || !this.showApplyValidation || this.formulaApplyValid) {
                return '';
            }
            if (!this.goodsFieldsValid) {
                return 'Corectați câmpurile marcate (cantitate, discount, valoare marfă), apoi apăsați din nou Aplică.';
            }
            if (!this.hasPositiveGoodsValue) {
                return 'Valoarea mărfii este 0 — % transport nu se poate calcula. Completați cantitatea sau o valoare marfă > 0.';
            }
            if (!this.hasPositiveCostTotal || !this.hasPositiveTransportRate) {
                return 'Total costuri sau % transport este 0. Adăugați costuri valide, apoi apăsați din nou Aplică.';
            }
            return 'Nu se poate aplica — verificați câmpurile marcate cu roșu.';
        },
        converterCollapsedHint() {
            const rate = this.converterRateDisplay;
            if (rate) {
                return `${this.fromCurrencyLabel} → ${this.toCurrencyLabel}: ${rate}`;
            }
            return 'opțional';
        },
        costsCollapsedHint() {
            const lines = this.regularCostLines;
            const count = lines.length;
            if (!count) {
                return 'niciun cost';
            }
            const total = lines.reduce((sum, line) => {
                const amount = this.lineAmountByKey[line.key];
                return sum + (amount > 0 ? amount : 0);
            }, 0);
            const totalLabel = total > 0
                ? this.formatMoneyAmount(total)
                : '';
            if (count === 1) {
                return totalLabel ? `1 cost · ${totalLabel}` : '1 cost';
            }
            return totalLabel
                ? `${count} costuri · ${totalLabel}`
                : `${count} costuri`;
        },
        customsCollapsedHint() {
            const line = this.customsDutyLine;
            if (!line) {
                return '';
            }
            if (!line.enabled) {
                return 'dezactivat';
            }
            const percent = String(line.value || '').trim();
            const amount = this.formatLineAmount(line.key);
            if (percent) {
                return amount !== '—'
                    ? `activ · ${percent}% = ${amount}`
                    : `activ · ${percent}%`;
            }
            return 'activ';
        },
        summary() {
            const computed = this.computedBreakdown;
            return {
                totalDisplay:
                    computed.total > 0
                        ? this.formatMoneyAmount(computed.total)
                        : '—',
                rateDisplay:
                    computed.rate > 0
                        ? `${ratioToPercentageInput(computed.rate, 2)}%`
                        : '—',
            };
        },
        /**
         * No enabled cost amounts — user reset / emptied the calculator.
         */
        isCalculatorCleared() {
            const lines = Array.isArray(this.draft?.lines) ? this.draft.lines : [];
            return !lines.some((line) => (
                line
                && line.enabled !== false
                && parseNumericInput(line.value) > 0
            ));
        },
        lineAmountByKey() {
            /** @type {Record<string, number>} */
            const map = {};
            (this.computedBreakdown.lineAmounts || []).forEach((line) => {
                map[line.key] = line.amount;
            });
            return map;
        },
        workingCurrencyCode() {
            return normalizeCurrencyCode(this.supplierCurrency || 'ron');
        },
        workingCurrencyLabel() {
            return currencyLabel(this.workingCurrencyCode);
        },
        fromCurrencyLabel() {
            return currencyLabel(this.draft.converterFromCurrency);
        },
        toCurrencyLabel() {
            return this.workingCurrencyLabel;
        },
        canConvertAmount() {
            return parseNumericInput(this.sourceAmount) > 0;
        },
        convertedAmountDisplay() {
            return this.convertedAmount > 0
                ? formatNumericInputValue(this.convertedAmount, 2)
                : '';
        },
        converterRateDisplay() {
            const rate = parseNumericInput(this.draft.converter);
            return rate > 0 ? formatNumericInputValue(rate, 2) : '';
        },
        regularCostLines() {
            return (this.draft.lines || []).filter((line) => !isCustomsDutyLine(line));
        },
        customsDutyLine() {
            return (this.draft.lines || []).find((line) => isCustomsDutyLine(line)) || null;
        },
        customsDutyLineIndex() {
            return (this.draft.lines || []).findIndex((line) => isCustomsDutyLine(line));
        },
    },
    watch: {
        modelValue(isOpen) {
            if (isOpen) {
                this.pendingExitFormulaApply = false;
                this.showApplyValidation = false;
                this.resetDraftFromProp();
            } else {
                this.currencyWatchReady = false;
                this.pendingExitFormulaApply = false;
                this.showApplyValidation = false;
            }
        },
        'draft.converterFromCurrency'() {
            this.onConverterCurrencyChanged();
        },
        supplierCurrency() {
            if (!this.modelValue) {
                return;
            }
            this.syncWorkingCurrency();
            if (this.currencyWatchReady) {
                this.onConverterCurrencyChanged();
            }
        },
        supplierListPriceNew() {
            // List price from builder drives auto goods — don't keep a stale lock.
            if (this.draft && !this.syncingAutoGoodsValue) {
                this.draft.goodsValueIsManual = false;
            }
            this.syncAutoGoodsValue();
        },
    },
    methods: {
        clearPendingExitFormulaApply() {
            if (this.pendingExitFormulaApply) {
                this.pendingExitFormulaApply = false;
            }
        },
        syncAutoGoodsValue() {
            if (this.draft.goodsValueIsManual) {
                return;
            }
            const next = applyAutoTransportGoodsValue(
                this.draft,
                this.transportGoodsContext,
            );
            if (String(this.draft.goodsValue ?? '') === String(next.goodsValue ?? '')) {
                return;
            }
            // Vuetify may emit update:model-value when we write goodsValue; that
            // must not latch goodsValueIsManual (locks mid-typing qty, e.g. 9900
            // → valoare marfă 1.978,22 → % transport 10× too high at 99000).
            this.syncingAutoGoodsValue = true;
            this.draft.goodsValue = next.goodsValue;
            this.$nextTick(() => {
                this.syncingAutoGoodsValue = false;
            });
        },
        onGoodsQuantityChanged(value) {
            this.clearPendingExitFormulaApply();
            this.draft.goodsQuantity = value;
            // Qty drives auto goods — drop a stale manual lock from typing.
            this.draft.goodsValueIsManual = false;
            this.syncAutoGoodsValue();
        },
        onGoodsDiscountChanged(value) {
            this.clearPendingExitFormulaApply();
            this.draft.goodsDiscount = value;
            this.draft.goodsValueIsManual = false;
            this.syncAutoGoodsValue();
        },
        onGoodsValueManualEdit(value) {
            if (this.syncingAutoGoodsValue) {
                return;
            }
            this.clearPendingExitFormulaApply();
            this.draft.goodsValue = value;
            const autoGoods = resolveEffectiveTransportGoodsValue(
                {
                    ...this.draft,
                    goodsValueIsManual: false,
                },
                this.transportGoodsContext,
            );
            const parsed = parseNumericInput(value);
            const canAuto = parseNumericInput(this.supplierListPriceNew) > 0
                && parseNumericInput(this.draft.goodsQuantity) > 0;
            // Same as calculated (incl. 0 at 100% discount / formatted write-back) → stay on auto.
            if (canAuto && Math.abs(parsed - autoGoods) <= 0.02) {
                this.draft.goodsValueIsManual = false;
                return;
            }
            this.draft.goodsValueIsManual = String(value ?? '').trim() !== '';
        },
        resetGoodsValueToCalculated() {
            this.draft.goodsValueIsManual = false;
            this.syncAutoGoodsValue();
        },
        onConverterCurrencyChanged() {
            if (!this.currencyWatchReady) {
                return;
            }
            this.syncWorkingCurrency();
            this.sourceAmount = '';
            this.convertedAmount = 0;
            this.convertError = '';
            this.convertWarning = '';
            this.converterMeta = '';
            this.draft.converter = '';
            void this.fetchDailyConverterRate();
        },
        syncWorkingCurrency() {
            this.draft.converterToCurrency = this.workingCurrencyCode;
        },
        formatMoneyAmount(amount, decimals = 2) {
            if (!(amount > 0)) {
                return '—';
            }
            return formatRonPrice(amount, decimals, this.workingCurrencyCode);
        },
        /**
         * Clear costs/conversions in the calculator only.
         * Does not touch the product builder — Aplică is required to persist.
         */
        resetAllToFresh() {
            if (this.readOnly) {
                return;
            }
            const next = createDefaultTransportCostBreakdown({
                mode: TRANSPORT_COST_MODE_FORMULA,
                converterFromCurrency: 'usd',
                converterToCurrency: this.workingCurrencyCode,
                converter: '',
                goodsQuantity: '',
                goodsDiscount: '',
                goodsValue: '',
                goodsValueIsManual: false,
                rateIsManual: false,
                lines: [],
            });
            ensureCustomsDutyLine(next);
            ensurePercentFeeBases(next);
            next.lines = orderTransportCostLines(next.lines);
            next.lines = next.lines.map((line) => (
                isCustomsDutyLine(line)
                    ? { ...line, enabled: false, value: line.value || '6.5' }
                    : line
            ));
            this.currencyWatchReady = false;
            this.draft = next;
            this.sourceAmount = '';
            this.convertedAmount = 0;
            this.convertError = '';
            this.convertWarning = '';
            this.converterMeta = '';
            this.costsSectionOpen = true;
            // Allow one Aplică on this empty state to exit formula mode on the product.
            this.pendingExitFormulaApply = true;
            this.showApplyValidation = false;
            this.converterSectionOpen = false;
            this.customsSectionOpen = false;
            this.$nextTick(() => {
                this.currencyWatchReady = true;
                void this.fetchDailyConverterRate();
            });
        },
        resetDraftFromProp() {
            const next = cloneTransportCostBreakdown(this.breakdown);
            next.mode = TRANSPORT_COST_MODE_FORMULA;
            if (!next.converterFromCurrency) {
                next.converterFromCurrency = 'usd';
            }
            next.converterToCurrency = normalizeCurrencyCode(
                this.supplierCurrency || 'ron',
            );
            // Normalize legacy "converted" lines to fixed amounts for display.
            next.lines = (next.lines || []).map((line) => {
                if (line.type === 'converted') {
                    const source = parseNumericInput(line.value);
                    const converter = parseNumericInput(next.converter);
                    const converted =
                        source > 0 && converter > 0 ? source * converter : source;
                    return createTransportCostLine({
                        ...line,
                        type: TRANSPORT_LINE_TYPE_FIXED,
                        value:
                            converted > 0
                                ? formatNumericInputValue(converted, 2)
                                : line.value,
                        sourceAmount: line.sourceAmount
                            || (source > 0 ? formatNumericInputValue(source, 2) : ''),
                        sourceCurrency: line.sourceCurrency
                            || next.converterFromCurrency
                            || '',
                        converterUsed: line.converterUsed
                            || (converter > 0
                                ? formatNumericInputValue(converter, 2)
                                : ''),
                    });
                }
                return createTransportCostLine(line);
            });
            ensureCustomsDutyLine(next);
            ensurePercentFeeBases(next);
            next.lines = orderTransportCostLines(next.lines);
            this.sourceAmount = '';
            this.convertedAmount = 0;
            this.convertError = '';
            this.convertWarning = '';
            this.converterMeta = '';
            this.currencyWatchReady = false;
            this.draft = next;
            // Heal a spurious goodsValueIsManual latch (e.g. from mid-typing qty):
            // if stored goods ≈ price×qty×discount, treat as auto and refresh.
            if (this.draft.goodsValueIsManual) {
                const autoGoods = resolveEffectiveTransportGoodsValue(
                    {
                        ...this.draft,
                        goodsValueIsManual: false,
                    },
                    this.transportGoodsContext,
                );
                const manualGoods = parseNumericInput(this.draft.goodsValue);
                if (
                    !(autoGoods > 0)
                    || Math.abs(manualGoods - autoGoods) <= 0.02
                ) {
                    this.draft.goodsValueIsManual = false;
                }
            }
            this.costsSectionOpen = true;
            this.converterSectionOpen = false;
            this.customsSectionOpen = false;
            this.syncAutoGoodsValue();
            this.$nextTick(() => {
                this.currencyWatchReady = true;
                void this.fetchDailyConverterRate();
            });
        },
        onCustomsEnabledChanged(enabled) {
            this.clearPendingExitFormulaApply();
            if (enabled) {
                this.customsSectionOpen = true;
            }
        },
        close() {
            this.pendingExitFormulaApply = false;
            this.showApplyValidation = false;
            this.$emit('update:modelValue', false);
        },
        apply() {
            this.syncWorkingCurrency();
            // After Resetează only: empty calculator exits formula mode (keep product %).
            if (this.isExitFormulaApply) {
                this.pendingExitFormulaApply = false;
                this.showApplyValidation = false;
                const payload = createDefaultTransportCostBreakdown({
                    mode: TRANSPORT_COST_MODE_PERCENT,
                    converterFromCurrency: this.draft.converterFromCurrency || 'usd',
                    converterToCurrency: this.workingCurrencyCode,
                    converter: '',
                    goodsQuantity: '',
                    goodsDiscount: '',
                    goodsValue: '',
                    goodsValueIsManual: false,
                    rateIsManual: true,
                    lines: [],
                });
                ensureCustomsDutyLine(payload);
                payload.lines = orderTransportCostLines(payload.lines).map((line) => (
                    isCustomsDutyLine(line)
                        ? { ...line, enabled: false, value: line.value || '6.5' }
                        : line
                ));
                this.$emit('apply', normalizeTransportCostBreakdown(payload));
                this.close();
                return;
            }
            if (!this.formulaApplyValid) {
                this.showApplyValidation = true;
                return;
            }
            this.pendingExitFormulaApply = false;
            this.showApplyValidation = false;
            const payload = applyAutoTransportGoodsValue(
                this.draft,
                this.transportGoodsContext,
            );
            payload.mode = TRANSPORT_COST_MODE_FORMULA;
            payload.converterToCurrency = this.workingCurrencyCode;
            ensureCustomsDutyLine(payload);
            ensurePercentFeeBases(payload);
            payload.lines = orderTransportCostLines(payload.lines);
            this.$emit('apply', normalizeTransportCostBreakdown(payload));
            this.close();
        },
        nextTransportLabel() {
            const used = new Set();
            (this.draft.lines || []).forEach((line) => {
                const match = /^transport\s+(\d+)$/i.exec(
                    String(line.label || '').trim(),
                );
                if (match) {
                    used.add(Number(match[1]));
                }
            });
            let n = 1;
            while (used.has(n)) {
                n += 1;
            }
            return `transport ${n}`;
        },
        lineIndexInDraft(lineKey) {
            return (this.draft.lines || []).findIndex((line) => line.key === lineKey);
        },
        insertBeforeCustomsLine(line) {
            this.clearPendingExitFormulaApply();
            const customsIdx = this.customsDutyLineIndex;
            if (customsIdx >= 0) {
                this.draft.lines.splice(customsIdx, 0, line);
            } else {
                this.draft.lines.push(line);
                ensureCustomsDutyLine(this.draft);
            }
        },
        addLineAfter(afterIndex) {
            this.clearPendingExitFormulaApply();
            const line = createTransportCostLine({
                label: this.nextTransportLabel(),
                type: TRANSPORT_LINE_TYPE_FIXED,
            });
            this.costsSectionOpen = true;
            if (afterIndex < 0 || !this.regularCostLines.length) {
                this.insertBeforeCustomsLine(line);
                return;
            }
            this.draft.lines.splice(afterIndex + 1, 0, line);
        },
        removeLine(index) {
            const removed = this.draft.lines[index];
            if (!removed || isCustomsDutyLine(removed)) {
                return;
            }
            this.clearPendingExitFormulaApply();
            this.draft.lines.splice(index, 1);
            this.draft.lines.forEach((line) => {
                line.baseLineKeys = (line.baseLineKeys || []).filter(
                    (key) => key !== removed.key,
                );
            });
            ensureCustomsDutyLine(this.draft);
            ensurePercentFeeBases(this.draft);
            this.draft.lines = orderTransportCostLines(this.draft.lines);
        },
        amountBaseLines(excludeKey) {
            return transportAmountBaseLines(this.draft, excludeKey);
        },
        toggleBaseLine(index, lineKey, checked) {
            const line = this.draft.lines[index];
            if (!line) {
                return;
            }
            this.clearPendingExitFormulaApply();
            const keys = new Set(line.baseLineKeys || []);
            if (checked) {
                keys.add(lineKey);
            } else {
                keys.delete(lineKey);
            }
            line.baseLineKeys = [...keys];
        },
        formatLineAmount(lineKey) {
            const amount = this.lineAmountByKey[lineKey];
            return this.formatMoneyAmount(amount);
        },
        lineSourceCaption(line) {
            if (!line) {
                return '';
            }
            const source = parseNumericInput(line.sourceAmount);
            if (!(source > 0) || !line.sourceCurrency) {
                return '';
            }
            const amount = formatNumericInputValue(source, 2);
            const currency = currencyLabel(line.sourceCurrency);
            const rate = parseNumericInput(line.converterUsed);
            if (rate > 0) {
                return `din ${amount} ${currency} (×${formatNumericInputValue(rate, 2)})`;
            }
            return `din ${amount} ${currency}`;
        },
        async fetchDailyConverterRate() {
            if (this.converterLoading) {
                return null;
            }
            this.syncWorkingCurrency();
            const from = this.draft.converterFromCurrency || 'usd';
            const to = this.workingCurrencyCode;
            if (String(from).toLowerCase() === String(to).toLowerCase()) {
                this.draft.converter = '1';
                this.converterMeta = `${this.fromCurrencyLabel} → ${this.toCurrencyLabel}`;
                this.convertError = '';
                this.convertWarning = '';
                return 1;
            }
            this.converterLoading = true;
            this.convertError = '';
            this.convertWarning = '';
            try {
                const response = await apiClient.get('/api/exchange-rates/daily/', {
                    params: { from, to },
                });
                const converter = Number(response?.data?.converter);
                if (!(converter > 0)) {
                    throw new Error('Convertor invalid');
                }
                const rounded = Math.round(converter * 100) / 100;
                this.draft.converter = formatNumericInputValue(rounded, 2);
                const date = response?.data?.date || '';
                const sourceRaw = String(response?.data?.source || '').toLowerCase();
                const source = sourceRaw === 'bnr'
                    ? 'BNR'
                    : sourceRaw === 'default'
                        ? 'setări listă'
                        : 'ECB';
                const rateLabel = formatNumericInputValue(rounded, 2);
                this.converterMeta = date
                    ? `${this.fromCurrencyLabel} → ${this.toCurrencyLabel}: ${rateLabel} (${source}, ${date})`
                    : `${this.fromCurrencyLabel} → ${this.toCurrencyLabel}: ${rateLabel} (${source})`;
                this.convertWarning = sourceRaw === 'default'
                    ? 'Curs zilnic indisponibil — se folosesc valorile implicite.'
                    : '';
                return rounded;
            } catch (error) {
                const fallback = resolveTransportConverterRate(
                    from,
                    to,
                    this.pricingSettings || {},
                );
                if (fallback > 0) {
                    const rounded = Math.round(fallback * 100) / 100;
                    this.draft.converter = formatNumericInputValue(rounded, 2);
                    this.converterMeta =
                        `${this.fromCurrencyLabel} → ${this.toCurrencyLabel}: `
                        + `${formatNumericInputValue(rounded, 2)} (setări listă)`;
                    this.convertWarning =
                        'Cursul zilnic BNR/ECB nu a putut fi încărcat — '
                        + 'se folosește cursul din setările listei.';
                    return rounded;
                }
                this.convertError =
                    error?.response?.data?.detail
                    || error?.message
                    || 'Nu am putut încărca cursul la zi.';
                return null;
            } finally {
                this.converterLoading = false;
            }
        },
        async convertAmount() {
            if (this.readOnly || this.converterLoading || !this.canConvertAmount) {
                return;
            }
            const converter = await this.fetchDailyConverterRate();
            if (!(converter > 0)) {
                this.convertedAmount = 0;
                return;
            }
            const source = parseNumericInput(this.sourceAmount);
            this.convertedAmount = Math.round(source * converter * 100) / 100;
        },
        addConvertedAsCost() {
            if (!(this.convertedAmount > 0)) {
                return;
            }
            const source = parseNumericInput(this.sourceAmount);
            const converter = parseNumericInput(this.draft.converter);
            const transportLine = createTransportCostLine({
                label: this.nextTransportLabel(),
                type: TRANSPORT_LINE_TYPE_FIXED,
                value: formatNumericInputValue(this.convertedAmount, 2),
                sourceAmount: source > 0
                    ? formatNumericInputValue(source, 2)
                    : '',
                sourceCurrency: this.draft.converterFromCurrency || '',
                converterUsed: converter > 0
                    ? formatNumericInputValue(converter, 2)
                    : '',
            });
            this.insertBeforeCustomsLine(transportLine);
            const customs = this.customsDutyLine;
            if (customs) {
                customs.baseGoodsValue = true;
                const keys = new Set(customs.baseLineKeys || []);
                keys.add(transportLine.key);
                customs.baseLineKeys = [...keys];
            }
            this.draft.lines = orderTransportCostLines(this.draft.lines);
            this.costsSectionOpen = true;
            this.sourceAmount = '';
            this.convertedAmount = 0;
        },
    },
};
</script>

<style scoped>
.transport-formula-card {
    display: flex;
    flex-direction: column;
    max-height: min(92vh, 900px);
}

.transport-formula-body {
    overflow-y: auto;
    flex: 1 1 auto;
}

.transport-collapse {
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    border-radius: 8px;
    padding: 8px 10px;
}

.transport-collapse__header {
    display: flex;
    align-items: center;
    width: 100%;
    border: 0;
    background: transparent;
    padding: 2px 0;
    cursor: pointer;
    text-align: left;
    color: inherit;
}

.transport-collapse__header--customs {
    gap: 8px;
}

.transport-collapse__toggle {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    border: 0;
    background: transparent;
    padding: 2px 0;
    cursor: pointer;
    text-align: left;
    color: inherit;
}

.transport-collapse__hint {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.transport-collapse__info {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 1;
    cursor: help;
}

.customs-duty-block--disabled {
    opacity: 0.72;
}

.transport-info-block {
    background-color: rgba(var(--v-theme-on-surface), 0.03);
    border-color: rgba(var(--v-theme-on-surface), 0.12) !important;
    min-height: 100%;
}

.transport-info-block--error {
    border-color: rgb(var(--v-theme-error)) !important;
}
</style>
